/**
 * Custom hook for managing patient meal plan assignments
 *
 * Handles fetching and managing meal plan assignments for a specific patient.
 * This hook uses getPatientMealPlans to get assignment data.
 *
 * @module hooks/usePatientMealPlanAssignments
 */

import { useEffect, useState, useCallback } from 'react';
import { apiService } from '@/services/ApiService';
import type { AssignMealPlanResponseFrontend, GetPatientMealPlansParams } from '@/types/mealPlan';
import { getLogger } from '@/services/shared/LoggerFactory';

interface UsePatientMealPlanAssignmentsProps {
  patientId?: string;
  params?: GetPatientMealPlansParams;
}

interface UsePatientMealPlanAssignmentsReturn {
  assignments: AssignMealPlanResponseFrontend[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  unassignMealPlan: (assignmentId: string) => Promise<void>;
}

/**
 * Custom hook for managing patient meal plan assignments
 */
export const usePatientMealPlanAssignments = ({
  patientId,
  params = {}
}: UsePatientMealPlanAssignmentsProps): UsePatientMealPlanAssignmentsReturn => {
  const logger = getLogger();

  // State management
  const [assignments, setAssignments] = useState<AssignMealPlanResponseFrontend[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch patient meal plan assignments
  const fetchAssignments = async () => {
    if (!patientId) {
      setError('No patient ID provided');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.mealplan.getPatientMealPlans(patientId, params);
      setAssignments(response);
    } catch (error) {
      logger.error('Failed to fetch patient meal plan assignments:', error);
      setError('Failed to load meal plan assignments');
    } finally {
      setLoading(false);
    }
  };

  // Fetch assignments when patientId or params change
  useEffect(() => {
    fetchAssignments();
  }, [patientId, JSON.stringify(params)]);

  // Refetch function
  const refetch = async () => {
    await fetchAssignments();
  };

  // Unassign meal plan function
  const unassignMealPlan = useCallback(async (assignmentId: string) => {
    try {
      await apiService.mealplan.removeAssignment(assignmentId);
      await refetch(); // Refresh the assignments after successful unassignment
    } catch (error) {
      logger.error('Failed to unassign meal plan:', error);
      throw error; // Re-throw so the UI can handle it if needed
    }
  }, [logger, refetch]);

  return {
    assignments,
    loading,
    error,
    refetch,
    unassignMealPlan,
  };
};